import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { createInterviewSession } from '../../utils/api';
import { getAuthToken } from '../../utils/cookies';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import InterviewTypeSelector from './components/InterviewTypeSelector';
import DurationDifficultySlider from './components/DurationDifficultySlider';
// Pilot disabled: Focus Topics selector is retained for post-pilot restore.
// import TopicSelector from './components/TopicSelector';
// import ConfigurationPreview from './components/ConfigurationPreview';
// import QuickStartCard from './components/QuickStartCard';
import InsufficientCoinsModal from './components/InsufficientCoinsModal';

import PrepInsights from './components/PrepInsights';
import SessionReadiness from './components/SessionReadiness';
import DashboardSuggestions from '../dashboard/components/DashboardSuggestions';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
const COINS_PER_MINUTE = 10;
const DEFAULT_COMPANY = 'Amazon';
const DEFAULT_DIFFICULTY = 'easy';
const PILOT_ALLOWED_DURATIONS = [15, 30];

const normalizeDifficulty = (value) => {
  const difficulty = String(value || '').toLowerCase();
  return ['easy', 'medium', 'hard'].includes(difficulty) ? difficulty : DEFAULT_DIFFICULTY;
};

const getDurationForDifficulty = (duration, difficulty, fallback = 30) => {
  const normalizedDifficulty = normalizeDifficulty(difficulty);
  return normalizedDifficulty === 'medium' || normalizedDifficulty === 'hard'
    ? 30
    : getPilotDuration(duration, fallback);
};

const getPilotDuration = (value, fallback = 30) => {
  const parsed = Number(value);
  return PILOT_ALLOWED_DURATIONS.includes(parsed) ? parsed : fallback;
};

const getInsufficientCoinsData = (error, fallbackRequired = 0) => {
  const status = error?.status || error?.response?.status;
  const errorCode = error?.error || error?.code || error?.response?.data?.error;
  const message = error?.message || error?.response?.data?.message || '';
  const isInsufficient =
    status === 402 ||
    errorCode === 'insufficient_coins' ||
    errorCode === 'insufficient_credits' ||
    /insufficient (coins|credits)|not enough (coins|credits)/i.test(message);

  if (!isInsufficient) return null;

  const data = error?.response?.data || error || {};
  return {
    required: Number(data.required ?? data.requiredCoins ?? data.needed ?? fallbackRequired ?? 0),
    current: Number(data.current ?? data.currentBalance ?? data.balance ?? 0),
  };
};

const InterviewSetup = () => {
   const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const planSession = location.state?.planSession || null;

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [availableTypes, setAvailableTypes] = useState([]);
  
  const [coachSuggestions, setCoachSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);

  const [isChallenge, setIsChallenge] = useState(false);
  const [challengeData, setChallengeData] = useState(null);

  const [config, setConfig] = useState({
    company: DEFAULT_COMPANY,
    role: 'Software Engineer',
    interviewType: '',
    duration: 30,
    difficulty: DEFAULT_DIFFICULTY,
    persona: 'friendly',
    advancedSettings: {
      topics: [],
      language: 'javascript',
      enableHints: false,
      realtimeFeedback: true,
      recordVideo: true
    }
  });

  useEffect(() => {
    const fetchCoachSuggestions = async () => {
      try {
        const cached = localStorage.getItem('prepxai_coach_suggestions');
        if (cached) {
          try {
            const parsedCache = JSON.parse(cached);
            const ageHours = (Date.now() - parsedCache.timestamp) / (1000 * 60 * 60);
            if (ageHours < 24 && parsedCache.data?.length > 0) {
              setCoachSuggestions(parsedCache.data);
              return; 
            }
          } catch (e) {}
        }
        
        const token = getAuthToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(`${API_BASE_URL}/api/coach/dashboard-suggestions`, {
          headers,
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          const suggestions = data || [];
          setCoachSuggestions(suggestions);
          
          if (suggestions.length > 0) {
            localStorage.setItem('prepxai_coach_suggestions', JSON.stringify({
              data: suggestions,
              timestamp: Date.now()
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch coach suggestions:', err);
      } finally {
        setLoadingSuggestions(false);
      }
    };
    
    fetchCoachSuggestions();
  }, []);

  useEffect(() => {
    const handleChallengeInit = async (challengeId) => {
      try {
        const token = getAuthToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(`${API_BASE_URL}/api/challenges/${challengeId}/details`, { headers, credentials: 'include' });
        if (res.ok) {
           const data = await res.json();
           const challenge = data.challenge;
           setChallengeData(challenge);
           setIsChallenge(true);
           
           // If challenge has a fixed config, use it
           if (data.config && data.config.id) {
               const cfg = data.config;
               setConfig(prev => ({
                 ...prev,
                 company: cfg.company || prev.company,
                 interviewType: cfg.interview_type || prev.interviewType,
                 difficulty: normalizeDifficulty(cfg.difficulty || cfg.Difficulty || prev.difficulty),
                 duration: getDurationForDifficulty(cfg.duration || prev.duration, cfg.difficulty || cfg.Difficulty || prev.difficulty),
               }));
           }
        }
      } catch (err) {
        console.error("Failed to load challenge details", err);
      }
    };

    const fetchTypes = async () => {
      try {
        const types = await import('../../utils/api').then(m => m.getInterviewTypes());
        setAvailableTypes(types);

        if (location.state?.challengeId) {
            handleChallengeInit(location.state.challengeId);
            return;
        }

        if (planSession) {
          /*
          Pilot disabled: restore plan topic prefill when Focus Topics returns.
          let planTopics = [];
          if (planSession.topics && Array.isArray(planSession.topics) && planSession.topics.length > 0) {
            planTopics = planSession.topics;
          } else if (planSession.topic) {
            planTopics = planSession.topic.split(',').map(t => t.trim()).filter(Boolean);
          }
          */

          setConfig(prev => ({
            ...prev,
            company: planSession.company || prev.company,
            interviewType: planSession.interview_type || (types[0]?.name || ''),
            difficulty: normalizeDifficulty(planSession.difficulty || prev.difficulty),
            duration: getDurationForDifficulty(planSession.duration || prev.duration, planSession.difficulty || prev.difficulty),
            advancedSettings: {
              ...prev.advancedSettings,
              // Pilot disabled: restore plan topic prefill when Focus Topics returns.
              // topics: planTopics.length > 0 ? planTopics : prev.advancedSettings.topics,
              topics: [],
            },
          }));
          return;
        }

        const savedConfig = localStorage.getItem('lastInterviewConfig');
        if (savedConfig) {
          try {
            const parsed = JSON.parse(savedConfig);
            setConfig(prev => ({
              ...prev,
              ...parsed,
              company: DEFAULT_COMPANY,
              difficulty: normalizeDifficulty(parsed.difficulty || prev.difficulty),
              duration: getDurationForDifficulty(parsed.duration || prev.duration, parsed.difficulty || prev.difficulty),
              advancedSettings: {
                ...prev.advancedSettings,
                ...(parsed.advancedSettings || {}),
                // Pilot disabled: clear saved focus topics while topic selection is hidden.
                topics: [],
              },
            }));
          } catch (error) {
            console.error('Failed to load saved configuration:', error);
          }
        } else if (types.length > 0) {
          setConfig(prev => ({ ...prev, interviewType: types[0].name }));
        }

        // --- Query Parameter Overrides ---
        // Usage: ?type=CODING&duration=30&topic=DP&topic=Recursion
        const qType = searchParams.get('type');
        const qDuration = searchParams.get('duration');
        // Pilot disabled: restore topic query parsing when Focus Topics returns.
        // const qTopics = searchParams.getAll('topic');

        if (qType || qDuration) {
          setConfig(prev => ({
            ...prev,
            interviewType: qType || prev.interviewType,
            duration: getDurationForDifficulty(qDuration ? parseInt(qDuration, 10) : prev.duration, prev.difficulty),
            company: prev.company || DEFAULT_COMPANY,
            advancedSettings: {
              ...prev.advancedSettings,
              // Pilot disabled: restore query topic overrides when Focus Topics returns.
              // topics: qTopics.length > 0 ? qTopics : prev.advancedSettings.topics
              topics: []
            }
          }));
        }
      } catch (error) {
        console.error('Failed to load interview types:', error);
      }
    };
    fetchTypes();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!config?.interviewType) newErrors.interviewType = 'Select interview format';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleStartInterview = async () => {
    if (!validateForm()) return;
    const token = getAuthToken();
    const coinsDeducted = !isChallenge && !planSession ? config.duration * COINS_PER_MINUTE : 0;
    setIsLoading(true);

    try {
      localStorage.setItem('lastInterviewConfig', JSON.stringify(config));
      
      let sessionId;
      if (isChallenge && location.state?.challengeId) {
          const token = getAuthToken();
          const res = await fetch(`${API_BASE_URL}/api/challenges/${location.state.challengeId}/start`, {
              method: 'POST',
              headers: { ... (token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
              credentials: 'include'
          });
          if (!res.ok) {
              const err = await res.json();
              throw { error: err.error || 'Failed to start challenge' };
          }
          const data = await res.json();
          sessionId = data.sessionId;
      } else {
          const payload = {
            role: config.role,
            duration: config.duration,
            company: config.company,
            difficulty: config.difficulty.toUpperCase(),
            type: config.interviewType,
            // Pilot disabled: Focus Topics selector is hidden during restricted pilot.
            // topics: config.advancedSettings.topics || [],
            topics: [],
            persona: config.persona || 'friendly',
            ...(planSession ? { is_plan_session: true, plan_enrollment_id: planSession.plan_enrollment_id, plan_day_id: planSession.plan_day_id } : {}),
          };

          const sessionResponse = await createInterviewSession(payload, token);
          sessionId = sessionResponse.session_id;
      }

      const sessionConfig = {
        ...config,
        coinsDeducted,
        coinsPerMinute: COINS_PER_MINUTE,
        isPlanSession: Boolean(planSession),
        isChallenge,
      };

      localStorage.setItem('currentInterviewSession', JSON.stringify({ ...sessionConfig, startTime: new Date()?.toISOString(), sessionId }));
      navigate('/pre-interview-check', { state: { sessionId: sessionId, config: sessionConfig, isChallenge } });
    } catch (error) {
      console.error('Failed to start interview:', error);
      const insufficientCoinsData = getInsufficientCoinsData(error, coinsDeducted);
      if (insufficientCoinsData) {
        setShowData(insufficientCoinsData);
        setShowBuyModal(true);
        setErrors({});
      } else if (error?.error === 'authorization required' || error?.status === 401) {
        navigate('/login', { state: { redirect: '/interview-setup' } });
      } else {
        setErrors({ form: error?.error || 'Failed to create session' });
      }
      setIsLoading(true); // Keep loading state if error handled by modal/redirect
    } finally {
        setIsLoading(false);
    }
  };

  const handleReset = () => {
    setConfig({
      company: DEFAULT_COMPANY,
      role: 'Software Engineer',
      interviewType: availableTypes[0]?.name || '',
      duration: 30,
      difficulty: DEFAULT_DIFFICULTY,
      persona: 'friendly',
      advancedSettings: {
        topics: [],
        language: 'javascript',
        enableHints: false,
        realtimeFeedback: true,
        recordVideo: true
      }
    });
    setErrors({});
  };

  const handleDifficultyChange = (difficulty) => {
    setConfig(prev => ({
      ...prev,
      difficulty,
      duration: getDurationForDifficulty(prev.duration, difficulty),
    }));
  };

  const [showData, setShowData] = useState({ required: 0, current: 0 });
  const [showBuyModal, setShowBuyModal] = useState(false);

  return (
    <>
      <Helmet>
        <title>Practice Hub - prepxai</title>
      </Helmet>
      <div className="min-h-screen bg-white text-slate-900 relative overflow-x-hidden">
        {/* Premium Header Background */}
        <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
        <NavigationHeader />

        <main className="pt-20 pb-64 px-6 relative z-10">
          <div className="max-w-full mx-auto px-4">
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: Insights */}
              <div className="lg:col-span-3 sticky top-20 h-fit space-y-6">
                <PrepInsights 
                  interviewType={config.interviewType}
                  suggestionsComponent={
                    <DashboardSuggestions suggestions={coachSuggestions} loading={loadingSuggestions} />
                  }
                />
              </div>

              {/* Center Column: Form */}
              <div className="lg:col-span-6 space-y-8">
                <div className="bg-white border border-slate-100 rounded-3xl p-8">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${isChallenge ? 'bg-emerald-600' : 'bg-slate-900'} flex items-center justify-center`}>
                          <Icon name={isChallenge ? "Trophy" : "Cpu"} size={20} color="#FFFFFF" className="fill-white" />
                      </div>
                      <div>
                          <h2 className="text-lg font-semibold text-slate-900">{isChallenge ? 'Challenge Entry' : 'Configure Session'}</h2>
                          <p className="text-xs font-normal text-slate-600 tracking-wide">
                            {isChallenge ? `Event: ${challengeData?.title || 'Live Challenge'}` : 'Setup your AI-powered interview'}
                          </p>
                      </div>
                    </div>
                    
                    {!isChallenge && (
                        <button 
                          onClick={handleReset}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all group"
                        >
                          <Icon name="RotateCcw" size={14} className="group-hover:rotate-[-90deg] transition-transform duration-300" />
                          <span className="text-[11px] font-normal tracking-widest">Reset</span>
                        </button>
                    )}
                  </div>

                  {isChallenge && (
                      <div className="mb-8 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                              <Icon name="Info" size={16} />
                          </div>
                          <p className="text-[12px] text-emerald-800 font-normal">
                              You are participating in a broadcasted challenge. Interview settings are fixed to ensure institutional standards.
                          </p>
                      </div>
                  )}

                  <div className="space-y-10">
                    <div className="space-y-6">
                        <h3 className="text-xs font-normal tracking-widest text-slate-900 ml-1">Select Interview Domain</h3>
                        <InterviewTypeSelector
                          value={config?.interviewType}
                          onChange={(value) => setConfig({ ...config, interviewType: value })}
                          error={errors?.interviewType}
                          types={availableTypes}
                        />
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-xs font-normal tracking-widest text-slate-900 ml-1">Difficulty</h3>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { value: 'easy', label: 'Easy', description: 'Build confidence' },
                            { value: 'medium', label: 'Medium', description: '30 min fixed' },
                            { value: 'hard', label: 'Hard', description: '30 min fixed' },
                          ].map(({ value, label, description }) => {
                            const isSelected = config.difficulty === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => handleDifficultyChange(value)}
                                aria-pressed={isSelected}
                                className={`rounded-xl border p-4 text-left transition-all ${isSelected ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                              >
                                <span className={`block text-sm font-medium ${isSelected ? 'text-emerald-800' : 'text-slate-900'}`}>{label}</span>
                                <span className="mt-1 block text-xs text-slate-500">{description}</span>
                              </button>
                            );
                          })}
                        </div>
                    </div>

                    <div className="space-y-6">
                        <h3 className="text-xs font-normal tracking-widest text-slate-900 ml-1">Interview Duration</h3>
                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                            <DurationDifficultySlider
                              duration={config?.duration}
                              difficulty={config?.difficulty}
                              onDurationChange={(value) => setConfig({ ...config, duration: value })}
                            />
                        </div>
                    </div>

                    {/* Pilot disabled: Focus Topics hidden during restricted pilot.
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100">
                      <TopicSelector
                        selectedTopics={config?.advancedSettings?.topics || []}
                        onTopicsChange={(topics) => setConfig({ 
                          ...config, 
                          advancedSettings: { ...config.advancedSettings, topics } 
                        })}
                        interviewType={config?.interviewType}
                      />
                    </div>
                    */}

                  </div>
                </div>
              </div>

              {/* Right Column: Readiness & Action */}
              <div className="lg:col-span-3 sticky top-20 h-fit">
                <SessionReadiness 
                    config={config} 
                    onStart={handleStartInterview} 
                    loading={isLoading} 
                    isPlanSession={Boolean(planSession)}
                    isChallenge={isChallenge}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      <InsufficientCoinsModal
        isOpen={showBuyModal}
        onClose={() => setShowBuyModal(false)}
        requiredCoins={showData.required}
        currentBalance={showData.current}
      />
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #10E58F;
          cursor: pointer;
          border: 2px solid white;
          transition: all 0.2s ease;
          margin-top: -6px; /* Center thumb on track */
        }
        .slider-thumb:hover::-webkit-slider-thumb {
          background: #059669;
          transform: scale(1.1);
          box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.1);
        }
        .slider-thumb::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #10E58F;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          border: none;
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default InterviewSetup;
